import { useEffect, useRef, useState } from "react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Message } from "primereact/message";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useParams } from "react-router-dom";
import MeaButton from "../../../../components/MeaButton";
import MeaStudentAvatar from "../../components/MeaStudentAvatar";
import MeaLink from "../../../../components/MeaLink";
import { useAuthContext } from "../../../../hooks/useAuthContext";
import slotChangeRequestsAPI from "../../../../services/slot_change_requests";
import slotsAPI from "../../../../services/slots";
import classesAPI from "../../../../services/classes";
import RejectSlotChangeRequestModal from "./RejectSlotChangeRequestModal";
import SlotChangeProposalAlgorithmHelpDialog from "./SlotChangeProposalAlgorithmHelpDialog";
import scheduleAPI from "../../../../services/schedule";
import {
  PERMISSION_TRAINING_CLASSES,
  PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS,
} from "../../../../permissions";
import {
  formatPreferredSlots,
  formatRequestDate,
  formatSlotLabel,
  matchTypeTag,
  slotsForClass,
  urgencyTag,
} from "../slotChangeUtils";

const SlotChangeProposalsComponent = () => {
  const params = useParams();
  const schoolId = params.schoolId;
  const trainingId = params.trainingId;
  const accessToken = "";
  const { hasPermission } = useAuthContext();
  const toast = useRef();
  const rejectModalRef = useRef();
  const [selected, setSelected] = useState([]);
  const [applying, setApplying] = useState(false);
  const [helpVisible, setHelpVisible] = useState(false);

  const proposalsQuery = slotChangeRequestsAPI.useGetProposals(
    schoolId,
    trainingId,
    accessToken
  );
  const slotsQuery = slotsAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000
  );
  const scheduleQuery = scheduleAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000,
    {}
  );
  const classesQuery = classesAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000,
    {}
  );
  const proposals = proposalsQuery?.data?.data?.proposals || [];
  const unassigned = proposalsQuery?.data?.data?.unassigned || [];
  const classImpacts = proposalsQuery?.data?.data?.class_impacts || [];
  const allClasses = classesQuery?.data?.data || [];

  const impactGroups = (() => {
    if (!classImpacts.length) {
      return [];
    }
    const impactByClassId = new Map(
      classImpacts.map((impact) => [impact.class_id, impact])
    );
    const impactedLevelIds = new Set(
      classImpacts
        .map((impact) => impact.level_id)
        .filter(Boolean)
    );
    const grouped = {};

    allClasses.forEach((classItem) => {
      if (!classItem?.level_id || !impactedLevelIds.has(classItem.level_id)) {
        return;
      }
      const groupKey = classItem.level_id;
      if (!grouped[groupKey]) {
        const impact = impactByClassId.get(classItem.id);
        grouped[groupKey] = {
          levelId: classItem.level_id,
          levelName:
            classItem?.level?.name || impact?.level_name || "Niveau",
          classes: [],
        };
      }

      const impact = impactByClassId.get(classItem.id);
      grouped[groupKey].classes.push({
        class_id: classItem.id,
        class_name: classItem.name,
        level_id: classItem.level_id,
        max_capacity: classItem.max_capacity || impact?.max_capacity || 0,
        occupancy_before: impact?.occupancy_before ?? classItem.total_students ?? 0,
        occupancy_after: impact?.occupancy_after ?? classItem.total_students ?? 0,
        incoming_count: impact?.incoming_count ?? 0,
        outgoing_count: impact?.outgoing_count ?? 0,
        remaining_capacity: impact?.remaining_capacity ??
          (classItem.max_capacity > 0
            ? classItem.max_capacity - (classItem.total_students ?? 0)
            : null),
        slots: impact?.slots || [],
      });
    });

    const groupedGroups = Object.values(grouped).sort((a, b) =>
      (a.levelName || "").localeCompare(b.levelName || "", "fr")
    );

    if (groupedGroups.length > 0) {
      return groupedGroups;
    }

    return [{
      levelId: "impacted",
      levelName: "Classes impactées",
      classes: classImpacts.map((impact) => ({
        ...impact,
        class_id: impact.class_id,
        class_name: impact.class_name,
      })),
    }];
  })();

  const getSourceSlots = (classId, snapshotSlot) => {
    if (snapshotSlot) {
      return [snapshotSlot];
    }
    return slotsForClass({
      slots: slotsQuery?.data?.data,
      scheduleItems: scheduleQuery?.data?.data,
      classId,
    });
  };

  useEffect(() => {
    setSelected(proposals);
  }, [proposalsQuery?.data]);

  const classPath = (classId) =>
    "/school/" +
    schoolId +
    "/admin/trainings/" +
    trainingId +
    "/classes/" +
    classId;

  const renderClassWithSlots = (classId, className, slotsFromApi) => {
    const classSlots =
      slotsFromApi?.length > 0
        ? slotsFromApi
        : slotsForClass({
            slots: slotsQuery?.data?.data,
            scheduleItems: scheduleQuery?.data?.data,
            classId,
          });
    if (!classId && !className && !classSlots.length) {
      return "-";
    }
    return (
      <div className="flex flex-column">
        {classId ? (
          <MeaLink to={classPath(classId)} permission={PERMISSION_TRAINING_CLASSES.GET}>
            {className || "Classe"}
          </MeaLink>
        ) : (
          className || "-"
        )}
        {classSlots.map((slot) => (
          <small key={slot.id || slot.name} className="text-500">
            {formatSlotLabel(slot)}
          </small>
        ))}
      </div>
    );
  };

  const renderStudentLink = (student, studentId) => {
    const resolved = student?.id
      ? student
      : studentId
        ? { ...student, id: studentId }
        : null;
    if (!resolved?.id) {
      return "-";
    }
    return (
      <MeaStudentAvatar
        student={resolved}
        schoolId={schoolId}
        trainingId={trainingId}
      />
    );
  };

  const renderCurrentClass = (request) => {
    const fromClass = request?.from_class;
    const fromSlots = request?.from_slot
      ? [request.from_slot]
      : slotsForClass({
          slots: [],
          scheduleItems: scheduleQuery?.data?.data,
          classId: fromClass?.id || request?.from_class_id || request?.student?.class_id,
        });

    if (!fromClass && !fromSlots.length) {
      return "-";
    }

    return (
      <div className="flex flex-column">
        {fromClass ? (
          <MeaLink
            to={classPath(fromClass.id)}
            permission={PERMISSION_TRAINING_CLASSES.GET}
          >
            {fromClass.name}
          </MeaLink>
        ) : (
          "-"
        )}
        {fromSlots.map((slot) => (
          <small key={slot.id || slot.name} className="text-500">
            {formatSlotLabel(slot)}
          </small>
        ))}
      </div>
    );
  };

  const handleApply = () => {
    if (!selected?.length) {
      toast.current.show({
        severity: "warn",
        summary: "Attention",
        detail: "Sélectionnez au moins une proposition",
      });
      return;
    }
    confirmDialog({
      message: `Appliquer ${selected.length} changement(s) de classe ?`,
      header: "Confirmation",
      icon: "pi pi-info-circle",
      acceptLabel: "Approuver",
      rejectLabel: "Annuler",
      accept: () => {
        setApplying(true);
        slotChangeRequestsAPI
          .Apply(
            schoolId,
            trainingId,
            accessToken,
            selected.map((item) => ({
              request_id: item.request?.id,
              to_class_id: item.to_class_id,
            }))
          )
          .then(() => {
            toast.current.show({
              severity: "success",
              summary: "Succès",
              detail: "Les changements ont été appliqués",
            });
            proposalsQuery.refetch();
          })
          .catch((err) => {
            toast.current.show({
              severity: "error",
              summary: "Erreur",
              detail: err.message,
            });
          })
          .finally(() => setApplying(false));
      },
    });
  };

  if (proposalsQuery.isError) {
    return (
      <Message
        severity="error"
        text={proposalsQuery.error?.message || "Erreur de chargement"}
      />
    );
  }

  return (
    <>
      <ConfirmDialog />
      <Toast ref={toast} position="bottom-center" />
      <SlotChangeProposalAlgorithmHelpDialog
        visible={helpVisible}
        onHide={() => setHelpVisible(false)}
      />
      <RejectSlotChangeRequestModal
        ref={rejectModalRef}
        schoolId={schoolId}
        trainingId={trainingId}
      />

      <div className="flex justify-content-between align-items-center mb-3">
        <div className="text-600">
          {proposals.length} proposition(s) — {unassigned.length} non affectée(s)
        </div>
        <div className="flex gap-2 align-items-center">
          <MeaButton
            icon="pi pi-question-circle"
            severity="help"
            outlined
            rounded
            tooltip="Expliquer l'algorithme de proposition"
            tooltipOptions={{ position: "top" }}
            aria-label="Expliquer l'algorithme de proposition"
            onClick={() => setHelpVisible(true)}
          />
          {hasPermission(PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS.APPLY) ? (
            <MeaButton
              label="Approuver la sélection"
              icon="pi pi-check"
              loading={applying}
              disabled={!selected?.length}
              onClick={handleApply}
              tooltip="Approuver les propositions sélectionnées"
              tooltipOptions={{ position: "top" }}
            />
          ) : null}
        </div>
      </div>

      <DataTable
        value={proposals}
        loading={proposalsQuery.isLoading}
        dataKey="request.id"
        selection={selected}
        onSelectionChange={(e) => setSelected(e.value)}
        emptyMessage="Aucune proposition. Créez des demandes de changement de créneau d'abord."
        className="mb-4"
      >
        <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
        <Column
          header="Élève"
          body={(item) =>
            renderStudentLink(item?.request?.student, item?.request?.student_id)
          }
        />
        <Column
          header="De"
          body={(item) => {
            const fromClass = item?.from_class;
            const fromSlots = getSourceSlots(
              fromClass?.id || item?.from_class_id,
              item?.from_slot || item?.request?.from_slot
            );
            if (!fromClass && !fromSlots.length) {
              return "-";
            }
            return (
              <div className="flex flex-column">
                {fromClass ? (
                  <MeaLink
                    to={classPath(fromClass.id)}
                    permission={PERMISSION_TRAINING_CLASSES.GET}
                  >
                    {fromClass.name}
                  </MeaLink>
                ) : (
                  "-"
                )}
                {fromSlots.map((slot) => (
                  <small key={slot.id || slot.name} className="text-500">
                    {formatSlotLabel(slot)}
                  </small>
                ))}
              </div>
            );
          }}
        />
        <Column
          header="Vers"
          body={(item) => (
            <div className="flex flex-column">
              <MeaLink
                to={classPath(item.to_class_id)}
                permission={PERMISSION_TRAINING_CLASSES.GET}
              >
                {item?.to_class?.name}
              </MeaLink>
              <small className="text-500">
                {formatSlotLabel(item?.requested_slot || item?.request?.requested_slot)}
                {item?.preference_rank > 1 ? ` · ${item.preference_rank}e choix` : ""}
              </small>
            </div>
          )}
        />
        <Column
          header="Date et heure"
          body={(item) => formatRequestDate(item?.request?.requested_at)}
        />
        <Column
          header="Urgence"
          body={(item) => {
            const tag = urgencyTag(item?.request?.urgency);
            return <Tag value={tag.value} severity={tag.severity} icon={tag.icon} style={tag.style} />;
          }}
        />
        <Column
          header="Type"
          body={(item) => {
            const tag = matchTypeTag(item?.match_type);
            return <Tag value={tag.value} severity={tag.severity} />;
          }}
        />
        <Column
          header="Effectif cible"
          body={(item) =>
            `${item.occupancy_before} → ${item.occupancy_after}` +
            (item.remaining_capacity != null
              ? ` (reste ${item.remaining_capacity})`
              : "")
          }
        />
      </DataTable>

      {impactGroups.length > 0 && (
        <>
          <h3 className="text-900 mt-4 mb-2">Impact sur les classes</h3>
          {impactGroups.map((group) => (
            <div key={group.levelId} className="mb-4">
              <div className="text-700 font-medium mb-2">Niveau : {group.levelName}</div>
              <DataTable value={group.classes} className="mb-3" size="small">
                <Column
                  header="Classe"
                  body={(item) =>
                    renderClassWithSlots(item.class_id, item.class_name, item.slots)
                  }
                />
                <Column
                  header="Effectif"
                  body={(item) => `${item.occupancy_before} → ${item.occupancy_after}`}
                />
                <Column field="incoming_count" header="Entrées" />
                <Column field="outgoing_count" header="Sorties" />
                <Column
                  header="Places restantes"
                  body={(item) =>
                    item.remaining_capacity != null ? item.remaining_capacity : "Illimité"
                  }
                />
              </DataTable>
            </div>
          ))}
        </>
      )}

      {unassigned.length > 0 && (
        <>
          <h3 className="text-900 mt-4 mb-2">Demandes sans solution</h3>
          <DataTable value={unassigned} emptyMessage="Aucune">
            <Column
              header="Élève"
              body={(item) =>
                renderStudentLink(item?.request?.student, item?.request?.student_id)
              }
            />
            <Column
              header="Classe actuelle"
              body={(item) => renderCurrentClass(item?.request)}
            />
            <Column
              header="Créneaux souhaités"
              body={(item) => formatPreferredSlots(item?.request)}
            />
            <Column
              header="Date et heure"
              body={(item) => formatRequestDate(item?.request?.requested_at)}
            />
            <Column
              header="Motif"
              body={(item) => item?.reason || "-"}
            />
            <Column
              header="Actions"
              body={(item) =>
                hasPermission(PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS.APPLY) ? (
                  <MeaButton
                    icon="pi pi-times"
                    rounded
                    text
                    severity="danger"
                    style={{ padding: "0", height: "1.75rem", width: "1.75rem" }}
                    tooltip="Refuser cette demande"
                    tooltipOptions={{ position: "top" }}
                    onClick={() =>
                      rejectModalRef.current?.show(
                        item?.request,
                        () => proposalsQuery.refetch(),
                        item?.reason || item?.request?.reason || ""
                      )
                    }
                  />
                ) : (
                  "-"
                )
              }
            />
          </DataTable>
        </>
      )}
    </>
  );
};

export default SlotChangeProposalsComponent;
